/**
 * Tab Navigation Helper
 * 
 * Utility functions for handling tab navigation from field search.
 * Extracts tab information from field paths and provides consistent
 * navigation behavior across all detail pages.
 */

/**
 * Extracts the tab name from a field path.
 * 
 * The field path format is: "TabName|CardName|FieldName"
 * This function extracts the first part (before the first pipe) as the tab name.
 * 
 * @param fieldPath - The path from field search (e.g., "MMI|Apportionment Detail|Address")
 * @returns The tab name or null if extraction fails
 * 
 * @example
 * extractTabFromFieldPath("MMI|Card|Field") // Returns "MMI"
 * extractTabFromFieldPath("MMI") // Returns "MMI"
 * extractTabFromFieldPath("Event|Loss|Field") // Returns "Event"
 * extractTabFromFieldPath("") // Returns null
 */
export function extractTabFromFieldPath(fieldPath: string | null | undefined): string | null {
  if (!fieldPath) {
    return null;
  }

  const parts = fieldPath.split('|').map(p => p.trim());
  
  if (parts.length === 0 || !parts[0]) {
    return null;
  }

  // Return the first part - tab-panel component will validate if it exists
  // If the tab doesn't exist, tab-panel will default to the first visible tab
  return parts[0];
}

/**
 * Extracts card name from a field path.
 * 
 * This is an optional utility for future card scrolling functionality.
 * The field path format is: "TabName|CardName|FieldName"
 * This function extracts the second part as the card name.
 * 
 * @param fieldPath - The path from field search
 * @returns The card name or null if not present
 * 
 * @example
 * extractCardFromFieldPath("MMI|Apportionment Detail|Address") // Returns "Apportionment Detail"
 * extractCardFromFieldPath("MMI") // Returns null
 */
export function extractCardFromFieldPath(fieldPath: string | null | undefined): string | null {
  if (!fieldPath) {
    return null;
  }

  const parts = fieldPath.split('|').map(p => p.trim());
  
  if (parts.length < 2 || !parts[1]) {
    return null;
  }

  return parts[1];
}

/**
 * Extracts field name from a field path.
 * 
 * This is an optional utility for future field highlighting functionality.
 * The field path format is: "TabName|CardName|FieldName"
 * This function extracts the third part as the field name.
 * 
 * @param fieldPath - The path from field search
 * @returns The field name or null if not present
 * 
 * @example
 * extractFieldFromFieldPath("MMI|Apportionment Detail|Address") // Returns "Address"
 * extractFieldFromFieldPath("MMI|Card") // Returns null
 */
export function extractFieldFromFieldPath(fieldPath: string | null | undefined): string | null {
  if (!fieldPath) {
    return null;
  }

  const parts = fieldPath.split('|').map(p => p.trim());
  
  if (parts.length < 3 || !parts[2]) {
    return null;
  }

  return parts[2];
}
